
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser } from '../../utils/auth';
import { getPatients, addVital, addLog, getPatientVitals, addEditRequest, updateVital } from '../../utils/storage';
import { getDeviceInfo, getIPAddress } from '../../utils/device';
import type { Patient, VitalReading } from '../../types';
import DashboardLayout from '../../components/feature/DashboardLayout';

export default function RBSEntry() {
  const navigate = useNavigate();
  const user = getUser();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [rbs, setRbs] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [alreadyRecorded, setAlreadyRecorded] = useState(false);
  const [existingVital, setExistingVital] = useState<VitalReading | null>(null);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [newRbs, setNewRbs] = useState('');
  const [updateReason, setUpdateReason] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/dashboard'); return; }
    if (!['rbs', 'admin', 'master_admin', 'all_vitals'].includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  if (!user) return null;

  const handleSearch = async (value: string) => {
    setSearchTerm(value);
    setSelectedPatient(null);
    setAlreadyRecorded(false);
    setExistingVital(null);
    if (value.trim().length > 0) {
      const patients = await getPatients();
      const results = patients.filter(
        (p) =>
          p.id.toLowerCase().includes(value.toLowerCase()) ||
          p.name.toLowerCase().includes(value.toLowerCase()) ||
          p.contactNumber.includes(value)
      );
      setSearchResults(results);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  const selectPatient = async (patient: Patient) => {
    setSelectedPatient(patient);
    setSearchTerm(`${patient.name} (${patient.id})`);
    setShowResults(false);
    const patientVitals = await getPatientVitals(patient.id);
    const rbsVital = patientVitals.find(v => v.type === 'rbs');
    if (rbsVital) {
      setAlreadyRecorded(true);
      setExistingVital(rbsVital);
    } else {
      setAlreadyRecorded(false);
      setExistingVital(null);
    }
    setRbs('');
    setError('');
  };

  const getRbsStatus = (value: number): { label: string; color: string } => {
    if (value < 70) return { label: 'Low (Hypoglycemia)', color: 'text-red-600' };
    if (value <= 140) return { label: 'Normal', color: 'text-green-600' };
    if (value <= 199) return { label: 'Pre-diabetic range', color: 'text-yellow-600' };
    return { label: 'High (Hyperglycemia)', color: 'text-red-600' };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!selectedPatient) { setError('Please select a patient first.'); return; }
    if (alreadyRecorded) { setError('RBS already recorded. Use "Request Update" to change it.'); return; }
    if (!rbs) { setError('Please enter a blood sugar value.'); return; }

    setLoading(true);
    try {
      const { browser, device } = getDeviceInfo();
      const ipAddress = await getIPAddress();
      const timestamp = new Date().toISOString();

      await addVital({
        id: `V${Date.now()}_RBS`,
        patientId: selectedPatient.id,
        type: 'rbs',
        value: rbs,
        unit: 'mg/dL',
        recordedAt: timestamp,
        recordedBy: user.username,
      });

      await addLog({
        id: Date.now().toString(),
        userId: user.id,
        username: user.username,
        action: 'RBS Entry',
        details: `Recorded RBS ${rbs} mg/dL for patient ${selectedPatient.name} (${selectedPatient.id})`,
        ipAddress, deviceName: device, browser, timestamp,
      });

      setSuccess(true);
      setTimeout(() => {
        setRbs(''); setSelectedPatient(null); setSearchTerm('');
        setSuccess(false); setAlreadyRecorded(false); setExistingVital(null);
      }, 2000);
    } catch (err) {
      console.error('Error recording RBS:', err);
      setError('Failed to record RBS. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRequest = async () => {
    if (!selectedPatient || !existingVital) return;
    if (!newRbs) { setError('Please enter a new RBS value.'); return; }
    if (!updateReason.trim()) { setError('Please provide a reason for the update.'); return; }

    try {
      const { browser, device } = getDeviceInfo();
      const ipAddress = await getIPAddress();
      const timestamp = new Date().toISOString();

      const requestId = `ER${Date.now()}`;

      await addEditRequest({
        id: requestId,
        patientId: selectedPatient.id,
        patientName: selectedPatient.name,
        vitalId: existingVital.id,
        vitalType: 'rbs',
        currentValue: existingVital.value,
        newValue: newRbs,
        requestedBy: user.username,
        requestedAt: timestamp,
        reason: updateReason,
        status: 'pending',
      });

      await updateVital(existingVital.id, { editRequested: true, editRequestId: requestId });

      await addLog({
        id: Date.now().toString(),
        userId: user.id,
        username: user.username,
        action: 'Edit Request',
        details: `Requested RBS update for ${selectedPatient.name} (${selectedPatient.id}): ${existingVital.value} → ${newRbs} mg/dL`,
        ipAddress, deviceName: device, browser, timestamp,
      });

      setShowUpdateModal(false);
      setNewRbs(''); setUpdateReason('');
      setUpdateSuccess(true);
      // Refresh existing vital to show edit pending badge
      const refreshed = await getPatientVitals(selectedPatient.id);
      const updated = refreshed.find(v => v.type === 'rbs');
      if (updated) setExistingVital(updated);
      setTimeout(() => setUpdateSuccess(false), 3000);
    } catch (err) {
      console.error('Error submitting update request:', err);
      setError('Failed to submit update request. Please try again.');
    }
  };

  const rbsNum = parseFloat(rbs);
  const rbsStatus = !isNaN(rbsNum) && rbs ? getRbsStatus(rbsNum) : null;

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Random Blood Sugar Entry</h2>
          <p className="text-sm text-gray-500 mt-1">Search for a patient and record blood sugar reading</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          {success && (
            <div className="mb-5 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-green-500 text-base"></i>
              Blood sugar reading recorded successfully!
            </div>
          )}
          {updateSuccess && (
            <div className="mb-5 bg-teal-50 border border-teal-200 text-teal-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <i className="ri-send-plane-line text-teal-500 text-base"></i>
              Update request submitted. Awaiting admin approval.
            </div>
          )}
          {error && (
            <div className="mb-5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500 text-base"></i>
              {error}
            </div>
          )}

          {/* Search */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Patient <span className="text-red-500">*</span></label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="ri-search-line text-gray-400 text-sm"></i>
              </div>
              <input type="text" value={searchTerm} onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchTerm && setShowResults(true)}
                placeholder="Search by ID, Name, or Mobile Number"
                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none text-sm" />
            </div>
            {showResults && searchResults.length > 0 && (
              <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto z-10 relative">
                {searchResults.map((patient) => (
                  <button key={patient.id} onClick={() => selectPatient(patient)}
                    className="w-full px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-left cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{patient.name}</p>
                        <p className="text-xs text-gray-500">{patient.id} • {patient.contactNumber}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                        patient.paymentStatus === 'paid'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {patient.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {showResults && searchResults.length === 0 && (
              <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3">
                <p className="text-sm text-gray-500">No patients found</p>
              </div>
            )}
          </div>

          {/* Selected Patient Info */}
          {selectedPatient && (
            <div className="mb-6 bg-rose-50 border border-rose-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-rose-800 mb-3 flex items-center gap-2">
                <i className="ri-user-line text-rose-600"></i>
                Selected Patient
              </h3>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <div><span className="text-gray-500">Name:</span> <span className="font-medium text-gray-800">{selectedPatient.name}</span></div>
                <div><span className="text-gray-500">ID:</span> <span className="font-medium text-gray-800">{selectedPatient.id}</span></div>
                <div><span className="text-gray-500">Age:</span> <span className="font-medium text-gray-800">{selectedPatient.age} yrs</span></div>
                <div><span className="text-gray-500">Gender:</span> <span className="font-medium text-gray-800">{selectedPatient.gender}</span></div>
                <div className="col-span-2 mt-1">
                  <span className="text-gray-500">Payment:</span>{' '}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ml-1 ${
                    selectedPatient.paymentStatus === 'paid'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    <i className={selectedPatient.paymentStatus === 'paid' ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'}></i>
                    {selectedPatient.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Already Recorded Banner */}
          {alreadyRecorded && existingVital && (
            <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <i className="ri-alert-line text-orange-500"></i>
                    <span className="text-sm font-semibold text-orange-800">RBS Already Recorded</span>
                    {existingVital.editRequested && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                        <i className="ri-time-line text-xs"></i>Edit Pending
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-orange-700">Current value: <span className="font-bold">{existingVital.value} mg/dL</span></p>
                  <p className="text-xs text-orange-600 mt-0.5">Recorded by {existingVital.recordedBy} on {new Date(existingVital.recordedAt).toLocaleString()}</p>
                </div>
                {!existingVital.editRequested && (
                  <button onClick={() => setShowUpdateModal(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-edit-line"></i>
                    Request Update
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Entry Form — only shown when not already recorded */}
          {!alreadyRecorded && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="border border-gray-200 rounded-lg p-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-drop-line text-rose-500 text-base"></i>
                  </div>
                  Blood Sugar Reading
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Random Blood Sugar <span className="text-gray-400 font-normal">(mg/dL)</span> <span className="text-red-500">*</span></label>
                  <input type="number" value={rbs} onChange={(e) => setRbs(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none text-sm"
                    placeholder="e.g. 110" min="20" max="600" />
                </div>
                {rbs && !isNaN(rbsNum) && (
                  <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Live Reading Preview</p>
                      <p className="text-xl font-bold text-rose-600">{rbs} mg/dL</p>
                    </div>
                    {rbsStatus && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500 mb-0.5">Status</p>
                        <p className={`text-sm font-semibold ${rbsStatus.color}`}>{rbsStatus.label}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={loading || !selectedPatient}
                  className="flex-1 bg-rose-500 hover:bg-rose-600 text-white font-medium py-2.5 rounded-lg transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm">
                  {loading ? 'Recording...' : 'Record Blood Sugar'}
                </button>
                <button type="button" onClick={() => navigate('/dashboard')}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* When already recorded, show a locked state with only cancel */}
          {alreadyRecorded && (
            <div className="pt-2">
              <button type="button" onClick={() => navigate('/dashboard')}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm">
                Back to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Update Request Modal */}
      {showUpdateModal && existingVital && selectedPatient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800">Request RBS Update</h3>
              <button onClick={() => setShowUpdateModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="mb-4 bg-gray-50 rounded-lg p-3 text-sm">
              <span className="text-gray-500">Current value:</span>
              <span className="ml-2 font-bold text-gray-800">{existingVital.value} mg/dL</span>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">New RBS (mg/dL) <span className="text-red-500">*</span></label>
              <input type="number" value={newRbs} onChange={(e) => setNewRbs(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 outline-none text-sm"
                placeholder="e.g. 105" min="20" max="600" />
            </div>
            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Reason for Update <span className="text-red-500">*</span></label>
              <textarea value={updateReason} onChange={(e) => { if (e.target.value.length <= 500) setUpdateReason(e.target.value); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 outline-none text-sm resize-none"
                rows={3} placeholder="Explain why this value needs to be updated..." maxLength={500} />
              <p className="text-xs text-gray-400 mt-1 text-right">{updateReason.length}/500</p>
            </div>
            <div className="flex gap-3">
              <button onClick={handleUpdateRequest}
                className="flex-1 bg-rose-500 hover:bg-rose-600 text-white font-medium py-2.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm">
                Submit Request
              </button>
              <button onClick={() => setShowUpdateModal(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
